// ═══════════════════════════════════════════════════════════════════════════
// debugReports — Library del módulo Debugging (19-may-2026)
// ───────────────────────────────────────────────────────────────────────────
// Bug tracker interno para Q&A. Permite a cualquier consultor:
//   - Reportar bugs con screenshot + descripción + severidad + categoría
//   - Comentar en reportes propios o ajenos (hilo bidireccional)
//   - Cambiar estado del reporte
//
// Auto-captura de contexto al crear el reporte:
//   - window.innerWidth × innerHeight  (clave para bugs responsive)
//   - userAgent (browser + OS)
//   - URL actual + título de página
//   - UID + email del reportador
//   - Empresa activa (si está en contexto de empresa)
//   - Últimos 5 errors de `clientLogs` del mismo user
//
// Storage:
//   - Doc Firestore en `debugReports/{reportId}`
//   - Subcolección `debugReports/{reportId}/comentarios`
//   - Screenshot en Firebase Storage `/debugReports/{reportId}.png`
//
// Notificaciones:
//   - Crear reporte → notifica a todos los admins (consultores Milbrands)
//   - Cambiar estado → notifica al reportador
//   - Comentar → notifica al otro lado (no a uno mismo)
// ═══════════════════════════════════════════════════════════════════════════

import {
  collection, query, where, orderBy, limit as fbLimit, getDocs, Timestamp,
} from 'firebase/firestore'
// Sin ciclo: proximoPaso.js no importa nada de este módulo (verificado).
import { grupoDe, recorteLimpio, etapaDe, claseMesaDe, proximoPasoDe } from './proximoPaso'
import { ref as storageRef, uploadBytes } from 'firebase/storage'
import { IMAGENES_PERMITIDAS, validarArchivoSubible } from '@core/lib/archivosSubibles'
import { httpsCallable } from 'firebase/functions'
import { db, storage, auth, functions } from '@core/lib/firebase'
import { createLogger, urlSegura } from '@core/lib/logger'
import { snapshotActual } from '@core/lib/snapshotMetricas'

// ADR 051 — TODA escritura/lectura de debugReports va por callables permisados
// (functions/debugging.js). El cliente NO escribe ni lee la colección directo
// (rules create/update/read:false). Enforcement server-side por requirePermiso.
const call = (name) => httpsCallable(functions, name)
const log = createLogger('debugReports')

// ─── Catálogos ────────────────────────────────────────────────────────────

export const SEVERIDADES = {
  BLOQUEANTE: 'bloqueante',  // 🔴 No puede seguir trabajando
  ALTA:       'alta',         // 🟠 UX importante afectada
  MEDIA:      'media',        // 🟡 Molesto pero workable
  BAJA:       'baja',         // ⚪ Cosmético
}

// `corto` (10-ago-2026, pedido Mariano): label compacto para la TABLA del
// listado — el completo va en el `title`. Molde de la tabla de Mercado (#594):
// encabezados/celdas cortos con el texto largo en el tooltip, para que la
// tabla entre sin scroll horizontal. Si un entry no define `corto`, la UI cae
// al `label` (fallback en los badges — un catálogo nuevo no rompe nada).
export const SEVERIDAD_LABELS = {
  bloqueante: { label: 'Bloqueante', corto: 'Bloq.', emoji: '🔴', color: 'red',    orden: 1 },
  alta:       { label: 'Alta',       emoji: '🟠', color: 'orange', orden: 2 },
  media:      { label: 'Media',      emoji: '🟡', color: 'amber',  orden: 3 },
  baja:       { label: 'Baja',       emoji: '⚪', color: 'zinc',   orden: 4 },
}

export const CATEGORIAS = {
  UI:          'ui',           // Layout, responsive, estilos
  UX:          'ux',           // Flow confuso, botón no anda
  DATA:        'data',         // Datos incorrectos / inconsistentes
  PERFORMANCE: 'performance',  // Lentitud
  CRASH:       'crash',        // Pantalla blanca o ErrorBoundary
  FEATURE:     'feature',      // No es bug, es feature request
}

export const CATEGORIA_LABELS = {
  ui:          { label: 'UI / Diseño',    corto: 'UI',      emoji: '🎨' },
  ux:          { label: 'UX / Flow',      corto: 'UX',      emoji: '🖱️' },
  data:        { label: 'Datos',          emoji: '📊' },
  performance: { label: 'Performance',    corto: 'Perf.',   emoji: '⚡' },
  crash:       { label: 'Crash / Error',  corto: 'Crash',   emoji: '💥' },
  feature:     { label: 'Feature request', corto: 'Feature', emoji: '💡' },
}

// Tipo de pedido (reporte KxKGNoPa, 3-jul-2026): distingue BUG (algo que debería
// andar y no anda) de NUEVA FUNCIONALIDAD (pedido de algo que hoy no existe), para
// que el procesamiento automático (loop nocturno IA) sepa qué le están pidiendo.
// Es un eje ORTOGONAL a `categoria` (que describe el área: UI/datos/crash/…).
export const TIPOS = {
  BUG:           'bug',            // 🐛 Algo roto o incorrecto
  FUNCIONALIDAD: 'funcionalidad',  // ✨ Pedido de algo nuevo / mejora
}

export const TIPO_LABELS = {
  bug:           { label: 'Bug',                 emoji: '🐛', color: 'red' },
  funcionalidad: { label: 'Nueva funcionalidad', corto: 'Func.', emoji: '✨', color: 'cyan' },
}

/** Tipo default según la categoría elegida (feature request → funcionalidad). */
export function tipoDefault(categoria) {
  return categoria === CATEGORIAS.FEATURE ? TIPOS.FUNCIONALIDAD : TIPOS.BUG
}

// Tope de tamaño para imágenes que el usuario SUBE a mano (reporte kUlsHDcy).
// Más chico que el techo de storage.rules (25 MB) — un print alcanza de sobra
// y evita subir fotos gigantes. La captura automática (html-to-image) no pasa
// por acá: siempre es un PNG chico.
export const MAX_IMG_ADJUNTA_BYTES = 5 * 1024 * 1024 // 5 MB

/**
 * PURO: valida una imagen que el usuario sube a mano al reportar/editar un bug.
 * Devuelve { ok:true } o { ok:false, error } — el caller decide cómo mostrarlo.
 * Espeja las reglas de Storage de `debugReports/staging` (las imágenes de
 * `IMAGENES_PERMITIDAS` — sin svg), con un tope propio más estricto
 * (MAX_IMG_ADJUNTA_BYTES).
 * @param {File|Blob} file
 */
export function validarImagenAdjunta(file) {
  // `image/*` a secas dejaba pasar svg, que las rules rebotan (T6) — y el
  // upload del staging traga el error (más abajo), así que el print se perdía
  // en silencio. La allowlist es el espejo de storage.rules.
  return validarArchivoSubible(file, {
    permitidos: IMAGENES_PERMITIDAS,
    maxBytes: MAX_IMG_ADJUNTA_BYTES,
    descripcion: 'una imagen (PNG, JPG, WebP)',
  })
}

// ── Origen del reporte (ADR 064, 6-ago-2026) ──────────────────────────────
// Quién lo levantó. Lo SELLA el server según el entrypoint — el cliente no lo
// manda y no lo puede falsificar (mismo patrón que `estado`).
//
// Para qué: cuando una sesión de Claude empieza a registrar los defectos que
// encuentra trabajando, sus hallazgos y los bugs que reporta una persona caen
// en la MISMA lista. Sin separarlos, la avalancha de IA entierra el triage
// humano — y encima distorsiona `contribucionesDe`, que rankea aportes por
// reportador y existe para medir a los analistas, no a un agente.
//
// Los reportes anteriores al campo no lo tienen: se leen como 'humano', que es
// lo que eran.
//
// ⚠️ SON TRES, no dos (report gs8gUjIsfLZvZ82NlzVw, 4-oct-2026). El bloque de
// arriba se escribió cuando el server sellaba `humano` e `ia`; después apareció
// `sistema` —los informes que fabrica el propio loop desde la telemetría y las
// métricas— y acá no se declaró. `origenDe` colapsaba a HUMANO todo lo que no
// fuera exactamente `ia`, así que un informe de cron era indistinguible de uno
// de una persona: el filtro «Persona» los devolvía y `contribucionesDe` los
// contaba. Lo que impide que vuelva a pasar NO es este comentario: es que la
// lista ahora es espejo de `functions/lib/vocabularioDebug.js` con un test de
// paridad que corre en «Frontend tests (vitest)» (`vocabulario.paridad.test.js`).
//
// El ORDEN de las claves es load-bearing: el candado compara con `toEqual`
// contra la lista del backend.
export const ORIGENES = {
  HUMANO:  'humano',
  IA:      'ia',
  SISTEMA: 'sistema',
}

export const ORIGEN_LABELS = {
  humano:  { label: 'Persona', emoji: '👤' },
  ia:      { label: 'IA',      emoji: '🤖' },
  // 🖥️ y no ⚙️: ese ya es el chip «En marcha» del filtro de acción de la misma
  // barra (`DebuggingPage.jsx`), y dos filtros vecinos con el mismo ícono para
  // cosas distintas es justo la confusión que el filtro viene a resolver.
  sistema: { label: 'Sistema', emoji: '🖥️' },
}

/**
 * Origen efectivo de un reporte.
 *
 * Por INCLUSIÓN, no por resta: devuelve el valor si es uno de los declarados en
 * `ORIGENES`, y HUMANO en cualquier otro caso. Antes era una ternaria contra
 * `ia`, o sea un conjunto definido como «todo lo que no es ia» — y un conjunto
 * por resta se queda silenciosamente mal el día que el enum crece, que es
 * exactamente lo que pasó con `sistema`.
 *
 * Lo DESCONOCIDO (sin campo, `null`, un valor que esta lista no declara) sigue
 * leyéndose como HUMANO a propósito, y eso no es un descuido heredado: es el
 * lado seguro elegido en el report 4YGRCknP4Mi7RvAxPDLe. El error caro sería
 * esconder el bug que reportó una persona; mostrar de más sólo molesta.
 */
export function origenDe(r) {
  const declarados = Object.values(ORIGENES)
  return r && declarados.includes(r.origen) ? r.origen : ORIGENES.HUMANO
}

export const ESTADOS = {
  INFORME:      'informe',        // 📋 Reporte SIN aprobar (analistas) — NO lo procesa el agente (fuera de ESTADOS_ACTIVOS). Aprobar → nuevo. (ADR 051)
  NUEVO:        'nuevo',          // Recién reportado
  EN_REVISION:  'en_revision',    // Admin lo leyó, está triaging
  EN_CURSO:     'en_curso',       // Dev está trabajando
  AJUSTADO_IA:  'ajustado_ia',    // 🤖 Agente nocturno aplicó el fix en el working tree — espera commit+deploy+validación humana (decisión Mariano 3-jul-2026)
  EN_VALIDACION:'en_validacion',  // Deployado — PARA que el reportador lo valide (label "Para validación", decisión Mariano 4-jul-2026; la clave queda en_validacion para no migrar docs existentes)
  CERRADO:      'cerrado',        // Resuelto y validado
  DESCARTADO:   'descartado',     // No era bug / no se va a hacer
  SIGUE_CON_ERRORES: 'sigue_con_errores', // ↩ El reportador validó y NO está resuelto — rebota al agente IA (candidato a fix nocturno junto con 'nuevo', decisión Mariano 3-jul-2026)
  // ── T0 Loop V2 (ADR 083) — espejo de ESTADOS de functions/lib/vocabularioDebug.js (la hoja que el motor re-exporta), atado por vocabulario.paridad.test.js.
  // La mayoría los pobla la maquinaria de T2+ (verificación, UAT, mesa);
  // `archivado` ya opera: es el repaso humano de cierres (lote del #641).
  DEPLOYADO:           'deployado',
  EN_VERIFICACION:     'en_verificacion',
  VERIFICADO_AUTO:     'verificado_auto',
  EN_UAT:              'en_uat',
  COLA_HUMANA:         'cola_humana',
  REFUTADO:            'refutado',
  CERRADO_SIN_VALIDAR: 'cerrado_sin_validar',
  ESCALADO:            'escalado',
  EN_MESA:             'en_mesa',
  PLANIFICADO:         'planificado',
  DIFERIDO:            'diferido',
  EN_INVESTIGACION:    'en_investigacion',
  CERRADO_EXPLICADO:   'cerrado_explicado',
  ARCHIVADO:           'archivado',
  EN_COMITE:           'en_comite', // flujo v2
}

export const ESTADO_LABELS = {
  informe:       { label: '📋 Informe',       color: 'slate',   orden: 0 },
  nuevo:         { label: 'Nuevo',           color: 'blue',    orden: 1 },
  en_revision:   { label: 'En revisión',     corto: 'Revisión', color: 'cyan',    orden: 2 },
  en_curso:      { label: 'En curso',        color: 'amber',   orden: 3 },
  ajustado_ia:   { label: '🤖 Ajustado por IA', corto: '🤖 Ajustado', color: 'pink',  orden: 4 },
  deployado:     { label: 'Deployado',       corto: 'Deploy',  color: 'sky',     orden: 5 },
  en_verificacion: { label: 'En verificación', corto: 'Verific.', color: 'indigo', orden: 6 },
  verificado_auto: { label: '✅ Verificado (auto)', corto: '✅ Verif.', color: 'teal', orden: 7 },
  en_uat:        { label: 'Para UAT',        corto: 'UAT',     color: 'violet',  orden: 8 },
  cola_humana:   { label: 'Cola humana',     corto: 'C.humana', color: 'orange', orden: 9 },
  refutado:      { label: 'Refutado',        color: 'rose',    orden: 10 },
  en_validacion: { label: 'Para validación',  corto: 'Validación', color: 'purple',  orden: 11 },
  cerrado:       { label: 'Cerrado',         color: 'emerald', orden: 12 },
  cerrado_sin_validar: { label: 'Cerrado sin validar', corto: 'C.s/val.', color: 'lime', orden: 13 },
  cerrado_explicado:   { label: 'Cerrado (explicado)', corto: 'C.expl.', color: 'green', orden: 14 },
  descartado:    { label: 'Descartado',      corto: 'Descart.', color: 'zinc',    orden: 15 },
  sigue_con_errores: { label: '↩ Sigue con errores', corto: '↩ Errores', color: 'red', orden: 16 },
  escalado:      { label: '🚨 Escalado',      color: 'red',     orden: 17 },
  en_mesa:       { label: 'En mesa',         color: 'cyan',    orden: 18 },
  planificado:   { label: 'Planificado',     corto: 'Planif.', color: 'blue',    orden: 19 },
  diferido:      { label: 'Diferido',        color: 'stone',   orden: 20 },
  en_investigacion: { label: 'En investigación', corto: 'Investig.', color: 'amber', orden: 21 },
  archivado:     { label: '🗄️ Archivado',     corto: 'Archiv.', color: 'neutral', orden: 22 },
  en_comite:     { label: '🧠 En comité',     corto: 'Comité',  color: 'fuchsia', orden: 23 },
}

// Estados "activos" — pendientes para el badge del sidebar Y los ÚNICOS que el
// agente nocturno procesa (espejo en functions/mcp/debuggingMcp.js). `informe`
// queda DELIBERADAMENTE afuera: es el candado fail-safe del triage gate (ADR 051)
// — un reporte sin aprobar nunca es visible para el agente.
export const ESTADOS_ACTIVOS = [ESTADOS.NUEVO, ESTADOS.EN_REVISION, ESTADOS.EN_CURSO, ESTADOS.AJUSTADO_IA, ESTADOS.EN_VALIDACION, ESTADOS.SIGUE_CON_ERRORES]

// Lo que /debugging carga al entrar: todo menos el ARCHIVO (report
// OIY9nj3ByKSO7eCDjejD). Decisión de Mariano, 16-oct-2026: «filtrar lo que ya
// está cerrado, traerlo sólo si lo pedimos» — reemplaza la del 21-ago («ver
// todo»). Medido el 18-oct en staging: 1051 de ~1590 reportes son `archivado`.
// Se excluye SÓLO `archivado` porque es el único estado que ninguna vista por
// defecto muestra ni cuenta ('activos' y 'todos' de `filtrarReportes` lo sacan,
// `conteoPorEtapa` lo descuenta): los cerrados sin archivar siguen llegando y
// la card «Cerrados» no cambia. La lista exacta la fija un test — un estado
// nuevo lo pone en rojo hasta que alguien decida de qué lado va.
export const ESTADOS_SIN_ARCHIVO = Object.values(ESTADOS).filter(e => e !== ESTADOS.ARCHIVADO)

/**
 * PURO. ¿Lo que se está mirando necesita el archivo cargado? Los promovidos
 * terminan `archivado`, y contribuciones y calibración cuentan el histórico
 * entero: sin el archivo darían otros números.
 */
export function requiereArchivo({ estado, vista } = {}) {
  return estado === ESTADOS.ARCHIVADO || estado === 'promovidos'
    || vista === 'contribuciones' || vista === 'calibracion'
}

// ─── Filtrado del listado (PURO — testeable) ──────────────────────────────
//
// Reporte cIU79fRY: + filtro por REPORTADOR ("filtrar por lo suyo" al validar).
// Extraído de DebuggingPage para testear la combinación de filtros sin montar
// el componente (regla: lógica fuera de las vistas).

/** Emails de reportadores distintos, ordenados — opciones del dropdown. */
export function reportadoresDe(reportes = []) {
  return [...new Set(reportes.map(r => r.reportadorEmail).filter(Boolean))].sort()
}

/** Secciones distintas, ordenadas — opciones del dropdown (reporte V7qoZp8V). */
export function seccionesDe(reportes = []) {
  return [...new Set(reportes.map(r => r.seccion).filter(Boolean))].sort()
}

/**
 * Aplica los filtros del listado de Debugging en el MISMO orden que la UI.
 * @param {Array} reportes
 * @param {{ estado?, severidad?, categoria?, tipo?, reportador?, seccion?, search? }} filtros
 */
// 'YYYY-MM-DD' (input type=date) → ms de la medianoche LOCAL de ese día.
// Local a propósito: las fechas de la tabla se muestran en hora local, y un
// filtro que corte en UTC dejaría afuera reportes de la noche.
function _msMedianocheLocal(yyyyMmDd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(yyyyMmDd || ''))
  if (!m) return null
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime()
}

// ── Lectura rápida (xcRatstl2jfIdUGwEVYC) ───────────────────────────────────
/**
 * PURO. Una línea que resume el caso para lectura humana rápida. Prioridad:
 * la línea de 140 del triage mecánico (T4 — ya sanitizada y con formato
 * `área · cita → implica`); si el doc no pasó por el triage (p. ej. se aprobó
 * antes de la corrida de las 19:00), fallback mecánico con la sección y el
 * arranque de la descripción. Sin IA: es un resumen estructural, no un juicio.
 */
export function lineaRapida(r) {
  // ⭐ El resumen en criollo manda sobre todo lo demás (21-ago-2026): es el
  // único de los tres textos escrito PARA QUIEN APRUEBA. Los otros dos —la
  // línea del triage y la descripción— están escritos para quien arregla, y
  // ésa es exactamente la confusión que Mariano nombró: *"parece clave morse"*.
  //
  // Va con el tag adelante, como lo pidió él: `[Técnico] …` / `[Producto] …`,
  // para poder barrer la tabla con la vista sin abrir nada.
  //
  // ⚠️ Sin esta línea, `enCriollo` sería un campo que se escribe y no se
  // muestra: la proyección lo trae y la card seguiría pintando la descripción
  // técnica. Un campo que ningún consumidor alcanza es la razón por la que
  // este mismo repo ya tuvo métricas que se escribían y morían en Firestore.
  const resumen = r?.enCriollo?.resumen
  if (resumen) {
    const etiqueta = (r?.tag || 'producto') === 'tecnico' ? 'Técnico' : 'Producto'
    return `[${etiqueta}] ${String(resumen).replace(/\s+/g, ' ').trim()}`
  }
  if (r?.triage?.linea) return r.triage.linea
  const seccion = r?.seccion || r?.categoria || ''
  // `recorteLimpio` y no un slice crudo: el de acá partía palabras.
  const corta = recorteLimpio(r?.descripcion, 110, 60)
  return [seccion, corta].filter(Boolean).join(' · ')
}

/**
 * PURO. El párrafo del pie de la card — dos o tres oraciones en prosa corrida.
 * `null` si el informe no lo trae, y entonces la card no pinta nada.
 *
 * Es el TERCER texto y no un recorte de los otros dos. `lineaRapida` es la
 * línea de la tabla; `enCriollo.detalle` es el expediente con sus cuatro
 * momentos; esto es lo que se lee de pie, en el teléfono, antes de firmar.
 *
 * Vive acá y no inline en las dos páginas que lo pintan: dos copias del mismo
 * fallback divergen, y la que quedara vieja dejaría el campo invisible en una
 * de las dos pantallas — que es justo la mitad que importa (`/guardia` es
 * desde donde se firma).
 *
 * Sólo se aplica a los informes NUEVOS: los que ya están en la cola no lo
 * tienen y no se backfillean (decisión de Mariano, 21-ago-2026).
 */
export function parrafoRapido(r) {
  const p = r?.enCriollo?.parrafo
  if (!p) return null
  const limpio = String(p).replace(/\s+/g, ' ').trim()
  return limpio || null
}

// ── Asistente de decisión de en_revision (bkYHpwxC29rpTAiwnOhT) ─────────────
export const RECOMENDACION_DECISION_LABELS = {
  nuevo:       { label: 'Mandarlo a la guardia (arreglar)', emoji: '🔧' },
  descartar:   { label: 'Descartarlo',                      emoji: '🗑️' },
  promover:    { label: 'Promoverlo al roadmap',            emoji: '✦' },
  sin_opinion: { label: 'Sin opinión (falta información)',  emoji: '🤷' },
}

/** Corre el asistente sobre HASTA 5 casos en revisión sin lectura (síncrono).
 * Con ~20 en cola son 4 clicks; lo nuevo lo cubre el cron de las 19:00. */
export async function asistirRevisiones() {
  const { data } = await call('asistirRevisiones')({})
  return data
}

// ── Highlights de "qué toca" (pedido Mariano 15-ago 19:22) ─────────────────
/**
 * PURO. Bullets que dicen QUÉ TOCA el reporte, para entender rápido el caso.
 * Derivado mecánico del `triage.locus` (paths → áreas) + gatillos — cero IA.
 * Sin locus: cae a la sección/categoría (mejor un bullet honesto que nada).
 */
const AREAS_LOCUS = [
  { re: /mercado-?libre|\bml[A-Z]|functions\/ml/i, label: 'Mercado Libre' },
  { re: /syncCore|syncAdapters|syncJobs|sync[A-Z]/, label: 'Sincro' },
  { re: /^(firestore|storage)\.rules$|firestore\.indexes/, label: 'Modelo de datos / rules' },
  { re: /^\.github\//, label: 'CI/CD' },
  { re: /^functions\/oauth|oauthCrypto/, label: 'OAuth / credenciales' },
  { re: /^src\/modules\/([^/]+)\//, label: (m) => `Módulo ${m[1]}` },
  { re: /^functions\//, label: 'Backend' },
  { re: /^scripts\//, label: 'Scripts' },
  { re: /^docs\//, label: 'Docs' },
]
const GATILLO_LABELS = {
  'zona-roja': '🔴 Zona roja (se firma de a uno)',
  'modelo-datos': 'Toca el modelo de datos',
  'backend-nuevo': 'Endpoint/function nueva',
  'cruza-modulos': 'Cruza más de un módulo',
}

export function highlightsDe(r) {
  const out = []
  const visto = new Set()
  const push = (x) => { if (x && !visto.has(x)) { visto.add(x); out.push(x) } }
  for (const path of (r?.triage?.locus || [])) {
    for (const a of AREAS_LOCUS) {
      const m = String(path).match(a.re)
      if (m) { push(typeof a.label === 'function' ? a.label(m) : a.label); break }
    }
  }
  for (const g of (r?.triage?.gatillos || [])) push(GATILLO_LABELS[g] || null)
  if (r?.triage?.tier === 'desconocido') push('Sin paths verificables — riesgo desconocido')
  if (out.length === 0 && r?.seccion) push(`Sección: ${r.seccion}`)
  if (out.length === 0 && r?.categoria) push(`Categoría: ${r.categoria}`)
  return out.slice(0, 6)
}

// ── Orden por columna (rI8LqGLZhxztQz5sIrAQ) ───────────────────────────────
// PURO. Los títulos de la tabla ordenan. Severidad y estado con orden
// SEMÁNTICO (bloqueante > alta > media > baja), el resto alfabético; fecha
// por epoch. `dir`: 'asc' | 'desc'.
const PESO_SEVERIDAD = { bloqueante: 0, alta: 1, media: 2, baja: 3 }
// `actualizadoEn` entra el 19-ago-2026 (report MTNJM3jUnAfMQduZq3mm, pedido de
// Mariano). El campo ya existía en el doc y el filtro de rango de fechas ya lo
// ofrecía como criterio ("Últ. modificación"); lo que faltaba era la columna.
export const COLUMNAS_ORDENABLES = ['creadoEn', 'actualizadoEn', 'estado', 'severidad', 'tipo', 'categoria', 'seccion', 'id', 'reportadorEmail']

export function ordenarPorColumna(rows = [], col = 'creadoEn', dir = 'desc') {
  if (!COLUMNAS_ORDENABLES.includes(col)) return rows
  const mult = dir === 'asc' ? 1 : -1
  const clave = (r) => {
    // Las dos fechas ordenan por TIEMPO, no por el string del timestamp: el
    // campo puede llegar como Timestamp de Firestore, `{_seconds}` o ISO según
    // por dónde entró el doc, y `String(...)` de un Timestamp es '[object
    // Object]' para todos — o sea, empate y orden arbitrario.
    if (col === 'creadoEn' || col === 'actualizadoEn') { const d = fechaDe(r[col]); return d ? d.getTime() : 0 }
    if (col === 'severidad') return PESO_SEVERIDAD[r.severidad] ?? 9
    return String(r[col] || '').toLowerCase()
  }
  return rows.slice().sort((a, b) => {
    const ka = clave(a); const kb = clave(b)
    if (ka < kb) return -1 * mult
    if (ka > kb) return 1 * mult
    return 0
  })
}

export function filtrarReportes(reportes = [], {
  estado = 'activos', severidad = 'todas', categoria = 'todas',
  tipo = 'todos', reportador = 'todos', seccion = 'todas', search = '',
  tag = 'todos',
  origen = 'todos',
  // ACCIÓN × ÁREA (ADR 2026-08-21) — el filtro principal de Mariano: qué
  // venís a hacer, cruzado con de qué parte del sistema es. `accion` filtra
  // por el GRUPO del próximo paso (lib/proximoPaso.js); `area` por el campo
  // que deriva el server (functions/lib/areaReporte.js).
  accion = 'todas', area = 'todas',
  // 4-sep-2026: la fila del sistema (etapa) y el subfiltro de la mesa (clase),
  // ADENTRO del filtro y no inline en la página — un solo mecanismo, con paridad
  // número ↔ click testeable sin montar React.
  etapa = 'todas', claseMesa = 'todas',
  // Rango de fechas (pedido Mariano 10-ago-2026): sobre creación o última
  // modificación, inclusive en las dos puntas ('hasta' incluye el día entero).
  fechaCampo = 'creadoEn', fechaDesde = '', fechaHasta = '',
} = {}) {
  let rows = reportes
  // ADR 051 — la vista humana incluye 'informe' (pendientes de aprobación) en
  // 'activos' para que los líderes los vean y triaguen. El AGENTE usa su propia
  // lista en debuggingMcp.js (sin informe), así el candado no se contamina.
  //
  // 22-ago-2026 (pedido de Mariano: "las cards deben reflejar la realidad"):
  // 'activos' pasa a definirse por la hoja de grupos — abierto = todo lo que NO
  // está en el grupo `archivar`. Antes era ESTADOS_ACTIVOS (6 estados) + informe,
  // y los 14 estados de T0 (en_mesa, escalado, en_uat, en_verificacion,
  // planificado…) quedaban INVISIBLES bajo el filtro default: trabajo vivo que
  // ningún filtro mostraba. Una sola fuente (grupoDe) para el filtro, las cards
  // y el digest — si divergen, divergen juntos y el test de paridad lo caza.
  if (estado === 'activos') rows = rows.filter(r => grupoDe(r) !== 'archivar')
  // 'promovidos' es un pseudo-estado: los promovidos terminan `archivado`, que
  // por default no se muestra, así que sin esta vista quedarían mezclados con
  // todo lo demás archivado. Se filtra por el PUNTERO (`roadmapItemId`), no por
  // estado: si mañana un promovido se reabre a `nuevo`, sigue siendo uno que
  // pasó al backlog y tiene que aparecer acá.
  else if (estado === 'promovidos') rows = rows.filter(r => !!r.roadmapItemId)
  // T0 (ADR 083): 'todos' EXCLUYE archivados — el archivo es "los ojos de
  // Mariano ya pasaron" y por default no vuelve a la vista; para verlos se
  // elige el estado `archivado` explícito en el dropdown (plan §3).
  else if (estado === 'todos') rows = rows.filter(r => r.estado !== ESTADOS.ARCHIVADO)
  else rows = rows.filter(r => r.estado === estado)
  if (severidad !== 'todas') rows = rows.filter(r => r.severidad === severidad)
  if (categoria !== 'todas') rows = rows.filter(r => r.categoria === categoria)
  if (tipo !== 'todos') rows = rows.filter(r => (r.tipo || tipoDefault(r.categoria)) === tipo)
  if (reportador !== 'todos') rows = rows.filter(r => r.reportadorEmail === reportador)
  if (seccion !== 'todas') rows = rows.filter(r => r.seccion === seccion) // V7qoZp8V
  if (area !== 'todas') rows = rows.filter(r => (r.area || 'otros') === area)
  if (accion !== 'todas') rows = rows.filter(r => grupoDe(r) === accion)
  if (etapa !== 'todas') rows = rows.filter(r => etapaDe(r) === etapa)
  if (claseMesa !== 'todas') rows = rows.filter(r => claseMesaDe(proximoPasoDe(r)) === claseMesa)
  // ADR 064 — separar lo que levantó una persona de lo que levantó la IA. Sin
  // esto, el volumen de hallazgos automáticos entierra el triage humano en la
  // misma lista.
  if (origen !== 'todos') rows = rows.filter(r => origenDe(r) === origen)
  if (fechaDesde || fechaHasta) {
    const campo = fechaCampo === 'actualizadoEn' ? 'actualizadoEn' : 'creadoEn'
    const desdeMs = _msMedianocheLocal(fechaDesde)
    const hastaMs = _msMedianocheLocal(fechaHasta)
    const hastaExcl = hastaMs != null ? hastaMs + 24 * 60 * 60 * 1000 : null
    rows = rows.filter(r => {
      const d = fechaDe(r[campo])
      // Con el filtro activo, un doc sin el campo no puede afirmar que cae en
      // el rango → queda afuera (mismo criterio que los demás filtros).
      if (!d) return false
      const ms = d.getTime()
      if (desdeMs != null && ms < desdeMs) return false
      if (hastaExcl != null && ms >= hastaExcl) return false
      return true
    })
  }
  // ⭐ Filtro por tag (21-ago-2026): separa lo que Mariano tiene que decidir
  // de lo que es decisión de ingeniería. El tag lo DERIVA el backend
  // (`functions/lib/enCriollo.js`) y acá sólo se filtra — sin espejo.
  //
  // El default `|| 'producto'` NO es cosmético: los informes previos al campo
  // no lo traen, y sin este default el filtro "Producto" los escondería a
  // TODOS. Un filtro que oculta sin decirlo es peor que no tenerlo.
  if (tag !== 'todos') {
    rows = rows.filter(r => (r.tag || 'producto') === tag)
  }
  if (search.trim()) {
    const needle = search.trim().toLowerCase()
    rows = rows.filter(r =>
      (r.descripcion || '').toLowerCase().includes(needle) ||
      (r.seccion || '').toLowerCase().includes(needle) ||
      (r.url || '').toLowerCase().includes(needle) ||
      (r.reportadorEmail || '').toLowerCase().includes(needle) ||
      // aGgmejzRNy48rEnbKBOB: la tabla ahora muestra el ID — el buscador lo
      // tiene que encontrar (Mariano intentó buscar por id y no podía).
      String(r.id || '').toLowerCase().includes(needle)
    )
  }
  return rows
}

// ─── Contribuciones por reportador (reporte c6uMTNYa — PURO, testeable) ────
//
// Ranking de quiénes más aportan al debugging, por mes o acumulado.
// Interpretación declarada en el hilo del reporte (4-jul-2026):
//   impacto  → mix de SEVERIDAD (bloqueante/alta/media/baja)
//   calidad  → % de reportes NO descartados (válidos sobre el total)

/**
 * PURO: normaliza un timestamp en cualquiera de sus formas (Firestore
 * Timestamp, {_seconds}, Date o ISO string) a Date, o null si no es fecha.
 * Fuente ÚNICA de la conversión — antes vivía copiada en `mesDe` y `fmtFecha`,
 * y el filtro por fecha iba a ser la tercera copia.
 */
export function fechaDe(ts) {
  if (!ts) return null
  const d = ts.toDate ? ts.toDate() : (ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts))
  return isNaN(d.getTime()) ? null : d
}

/** 'YYYY-MM' del timestamp (Firestore Timestamp, {_seconds}, Date o ISO). */
export function mesDe(ts) {
  const d = fechaDe(ts)
  if (!d) return null
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

/** Meses 'YYYY-MM' con al menos un reporte, descendente — selector de la solapa. */
export function mesesConReportes(reportes = []) {
  return [...new Set(reportes.map(r => mesDe(r.creadoEn)).filter(Boolean))].sort().reverse()
}

/**
 * Agrega los reportes por reportador (opcionalmente de un mes 'YYYY-MM').
 * Devuelve filas ordenadas por total desc:
 *   { reportador, total, bugs, funcionalidades,
 *     severidad: { bloqueante, alta, media, baja },
 *     descartados, cerrados, calidadPct }
 */
export function contribucionesDe(reportes = [], { mes = 'todos' } = {}) {
  // ADR 064 — los reportes de IA quedan FUERA del ranking. Esta métrica existe
  // para medir el aporte de los analistas: meter un agente que puede levantar
  // decenas de hallazgos por sesión la vuelve inservible para lo único que se
  // usa. El volumen de la IA se mide aparte, con el filtro por origen.
  const soloHumanos = (reportes || []).filter(r => origenDe(r) === ORIGENES.HUMANO)
  const rows = mes === 'todos' ? soloHumanos : soloHumanos.filter(r => mesDe(r.creadoEn) === mes)
  const por = new Map()
  for (const r of rows) {
    const email = r.reportadorEmail || r.reportadorUid || '—'
    let e = por.get(email)
    if (!e) {
      e = {
        reportador: email, total: 0, bugs: 0, funcionalidades: 0,
        severidad: { bloqueante: 0, alta: 0, media: 0, baja: 0 },
        descartados: 0, cerrados: 0,
      }
      por.set(email, e)
    }
    e.total += 1
    const tipo = r.tipo || tipoDefault(r.categoria)
    if (tipo === TIPOS.FUNCIONALIDAD) e.funcionalidades += 1
    else e.bugs += 1
    if (e.severidad[r.severidad] != null) e.severidad[r.severidad] += 1
    if (r.estado === ESTADOS.DESCARTADO) e.descartados += 1
    if (r.estado === ESTADOS.CERRADO) e.cerrados += 1
  }
  return [...por.values()]
    .map(e => ({ ...e, calidadPct: e.total > 0 ? Math.round(((e.total - e.descartados) / e.total) * 100) : null }))
    .sort((a, b) => b.total - a.total || a.reportador.localeCompare(b.reportador))
}

// ─── Tipos de notificación (extender el catálogo en core/lib/notificaciones) ──

const TIPO_NOTIF_REPORTE_NUEVO    = 'debug_reporte_nuevo'
const TIPO_NOTIF_ESTADO_CAMBIO    = 'debug_estado_cambio'
const TIPO_NOTIF_COMENTARIO_NUEVO = 'debug_comentario_nuevo'

// ─── Auto-captura de contexto ─────────────────────────────────────────────

/**
 * Captura datos del browser + página + últimos errores del cliente.
 * Se ejecuta sync (los últimos errores hacen 1 query async opcional).
 */
export async function capturarContexto({ empresaId = null } = {}) {
  const ctx = {
    // Browser / viewport
    innerWidth:  typeof window !== 'undefined' ? window.innerWidth  : null,
    innerHeight: typeof window !== 'undefined' ? window.innerHeight : null,
    devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : null,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    // Página. La URL va por `urlSegura`: el reporte lo lee todo el que triagea
    // y no vence, y hay pantallas con la credencial en la URL (report
    // mv9ZeJh5fJ7bqTUA6eIr). La sección se deriva acá, de la URL entera,
    // porque la limpia ya no trae los valores de `tab` y `dashId`.
    url:    typeof window !== 'undefined' ? urlSegura(window.location.href) : null,
    titulo: typeof document !== 'undefined' ? document.title : null,
    seccion: typeof window !== 'undefined'
      ? inferirSeccion(window.location.href, typeof document !== 'undefined' ? document.title : null)
      : null,
    // Usuario
    uid:   auth.currentUser?.uid || null,
    email: auth.currentUser?.email || null,
    // Empresa activa (si la pasa el caller)
    empresaId,
    // Build / versión
    buildHash: typeof __APP_BUILD__ !== 'undefined' ? __APP_BUILD__ : null,
    // Timestamp del cliente
    capturadoEnCliente: new Date().toISOString(),
    // T7-g (§6.2): lo que las pantallas publicaron al registro central —
    // métricas con ventana ABSOLUTA y filtros. `origenes: []` = nadie
    // publicaba (≠ reporte pre-snapshot, que no trae el campo y es "no
    // reproducible" por defecto). Best-effort: el FAB jamás se cae por esto.
    snapshot: null,
    // Últimos errores del clientLogs del mismo user (best-effort)
    ultimosErrores: null,
  }
  try { ctx.snapshot = snapshotActual() } catch { /* best-effort declarado */ }
  try {
    const uid = auth.currentUser?.uid
    if (uid) {
      const hace1h = Timestamp.fromMillis(Date.now() - 60 * 60 * 1000)
      const q = query(
        collection(db, 'clientLogs'),
        where('uid', '==', uid),
        where('level', '==', 'error'),
        where('timestamp', '>=', hace1h),
        orderBy('timestamp', 'desc'),
        fbLimit(5),
      )
      const snap = await getDocs(q)
      ctx.ultimosErrores = snap.docs.map(d => {
        const data = d.data()
        return {
          scope: data.scope || null,
          errorName: data.errorName || null,
          errorMessage: data.errorMessage || null,
          url: data.url || null,
          timestamp: data.timestamp || null,
        }
      })
    }
  } catch { /* graceful, no bloquear el reporte */ }
  return ctx
}

/**
 * Deriva un nombre legible para la sección a partir de la URL + título.
 * Ej: "https://.../empresas/sdXo.../?tab=dash-todos&dashId=ml-publicidad"
 *     → "Empresa · Dashboards · Publicidad"
 */
export function inferirSeccion(url, titulo) {
  if (titulo && titulo !== 'Milbrands CoWork') return titulo
  if (!url) return 'Desconocida'
  try {
    const u = new URL(url)
    const partes = u.pathname.split('/').filter(Boolean)
    const tab = u.searchParams.get('tab')
    const dashId = u.searchParams.get('dashId')
    let label = partes[0] || 'home'
    if (partes[0] === 'empresas' && partes[1]) label = 'empresa'
    if (tab) label += ` · ${tab}`
    if (dashId) label += ` · ${dashId.replace(/^ml-/, '')}`
    return label
  } catch { return url.slice(0, 80) }
}

// ─── Crear reporte ────────────────────────────────────────────────────────

/**
 * Crea un reporte de bug vía el callable `crearReporteDebug` (permission-driven).
 * El SERVER sella el estado: SIEMPRE 'informe' (decisión Mariano 11-ago-2026 —
 * el fast-path `crearAprobado` quedó eliminado; la única puerta a 'nuevo' es
 * `debugging.autorizar` vía aprobarReporte). El screenshot se sube primero a
 * staging del propio uid; el callable lo mueve a su lugar canónico y guarda el
 * path (jamás URL pública).
 * @returns {Promise<{id:string, estado:string}>}
 */
export async function crearReporte({
  screenshotBlob,
  screenshotContentType = 'image/png',
  descripcion,
  severidad,
  categoria,
  tipo,
  contexto,
}) {
  if (!auth.currentUser) throw new Error('Auth required')
  if (!descripcion?.trim()) throw new Error('descripcion requerida')

  // 1) Screenshot → staging del propio uid (Storage rules lo permiten; el callable valida el path).
  let screenshotPath = null
  if (screenshotBlob) {
    try {
      const ct = screenshotContentType || 'image/png'
      const ext = ct === 'image/jpeg' ? 'jpg' : ct === 'image/webp' ? 'webp' : 'png'
      const nombre = `${Date.now()}-${Math.round(Math.random() * 1e9)}.${ext}`
      screenshotPath = `debugReports/staging/${auth.currentUser.uid}/${nombre}`
      await uploadBytes(storageRef(storage, screenshotPath), screenshotBlob, { contentType: ct })
    } catch (err) {
      screenshotPath = null // no bloquea el reporte
      log.warn('staging screenshot fallo', { error: err.message })
    }
  }

  const seccion = contexto?.seccion || inferirSeccion(contexto?.url, contexto?.titulo)
  const res = await call('crearReporteDebug')({
    descripcion: descripcion.trim(), severidad, categoria, tipo, contexto: contexto || null,
    seccion, screenshotPath,
  })
  return { id: res.data.id, estado: res.data.estado }
}

/**
 * PURO: ¿este uid puede editar el reporte? Autor, o admin global. (Se mantiene
 * para tests/UI; la edición post-creación quedó DIFERIDA en el rework ADR 051.)
 */
export function puedeEditarReporte(reporte, uid, esAdmin = false) {
  if (!reporte) return false
  if (esAdmin) return true
  return !!(uid && reporte.reportadorUid === uid)
}

// ─── Listar / ver (lectura por callable — sin realtime, ADR 051) ────────────

/**
 * Lista de reportes (metadata, sin screenshot) via `listarReportesDebug`.
 *
 * Devuelve `{reportes, total, truncado}`, no el array pelado: `total` es el
 * tamaño REAL del conjunto y `reportes` la ventana. Los dos números tienen que
 * viajar juntos o el que los muestre no puede saber que está mintiendo — hasta
 * el 14-ago-2026 el server devolvía `total: reportes.length` y el badge de
 * `/guardia` contaba la ventana creyendo que contaba la cola.
 *
 * `estados` filtra SERVER-SIDE (T4 PR-2a). Omitirlo = sin filtro, el
 * comportamiento histórico.
 *
 * @param {number} limite
 * @param {string[]} [estados]
 * @returns {Promise<{reportes: object[], total: number, truncado: boolean}>}
 */
export async function cargarReportes(limite = 200, estados, cursor) {
  const args = { limite }
  if (estados) args.estados = estados
  if (cursor) args.cursor = cursor
  const res = await call('listarReportesDebug')(args)
  const d = res.data || {}
  const reportes = d.reportes || []
  // Backend viejo (ventana de deploy: hosting sale ~5 min antes que functions)
  // ⇒ no manda `total` ni `truncado`. Se degrada al comportamiento de hoy en
  // vez de renderizar `undefined`.
  return {
    reportes,
    total: Number.isFinite(d.total) ? d.total : reportes.length,
    truncado: d.truncado === true,
    cursorProximo: d.cursorProximo || null,
  }
}

/**
 * El TECHO de la carga completa. Decisión de Mariano (21-ago-2026): prefiere
 * ver todo y que la pantalla avise sólo si algo se dispara. Desde el 16-oct
 * «todo» es todo menos el archivo, que se trae a pedido (`ESTADOS_SIN_ARCHIVO`).
 *
 * Con 536 reportes al 21-ago son 2 llamadas, igual que hoy. El número existe
 * para que un crecimiento anómalo no cuelgue la pantalla en silencio: al
 * tocarlo se avisa, con el mismo cartel que ya existía.
 *
 * ⚠️ Si algún día hay que subirlo, lo primero que hay que bajar es el TAMAÑO DE
 * PÁGINA, no el techo: la respuesta de un callable topea en 10 MB y a 500 docs
 * por página el peor caso ya está en ~5 MB (`descripcion` ≤5000 +
 * `enCriollo.detalle` ≤4000 por reporte).
 */
export const TECHO_CARGA = 5000
const TAMANO_PAGINA = 500

/**
 * La lista COMPLETA, paginando con cursor hasta el final o hasta `TECHO_CARGA`.
 *
 * Existe porque la pantalla filtra y ordena EN CLIENTE por 9 criterios: con una
 * ventana parcial, cada uno de esos filtros corre sobre un subconjunto
 * arbitrario y los más viejos no existen para ninguno. Al 21-ago-2026 eran 36
 * reportes invisibles de 536, y crecía todos los días.
 *
 * Acumula en un array local y devuelve UNA sola vez, en vez de ir publicando
 * cada página: las cards de arriba cuentan sobre `reportes.length`, así que un
 * render progresivo mostraría 500 → 1000 → 1536 con el sort y los filtros
 * recalculándose sobre conjuntos parciales. Números que se mueven solos en
 * pantalla es exactamente lo que esto viene a evitar.
 *
 * Con `estados`, la carga es de ese subconjunto (filtro del server) y `total`
 * cuenta ese mismo subconjunto. Con filtro, el doc del cursor puede cambiar de
 * estado entre una página y la siguiente, y el server lo rechaza
 * (`cursorEnFiltro`): se vuelve a empezar UNA vez, como pide su mensaje; un
 * segundo rechazo se propaga.
 *
 * @param {(cargados: number, total: number) => void} [onProgreso] Para el cartel de "cargando N de M".
 * @param {string[]} [estados] Sin él, la colección entera.
 * @returns {Promise<{reportes: object[], total: number, truncado: boolean}>}
 *   `truncado` acá significa **paré antes de llegar al final** — o porque toqué
 *   el techo, o porque el server dijo que quedaba más y ya no pido. NO es el
 *   `truncado` de una página suelta.
 */
export async function cargarTodosLosReportes(onProgreso, estados) {
  const primera = await cargarPaginando(onProgreso, estados)
  if (!primera.cursorRechazado) return primera
  const segunda = await cargarPaginando(onProgreso, estados)
  if (segunda.cursorRechazado) throw segunda.cursorRechazado
  return segunda
}

async function cargarPaginando(onProgreso, estados) {
  const acumulado = []
  let cursor = null
  let total = 0

  // El tope de vueltas es una guarda de bucle, no la regla: la regla es
  // `TECHO_CARGA`. Existe por lo mismo que la heurística de página del server
  // —que el paginado TERMINE— y porque un `cursorProximo` que se repitiera
  // dejaría a la pantalla girando sin que nadie lo note.
  const MAX_VUELTAS = Math.ceil(TECHO_CARGA / TAMANO_PAGINA) + 1

  for (let vuelta = 0; vuelta < MAX_VUELTAS; vuelta++) {
    let pagina
    try {
      pagina = await cargarReportes(TAMANO_PAGINA, estados, cursor)
    } catch (err) {
      // Sin filtro de estado ningún cambio de estado invalida el cursor: ahí
      // el rechazo es otra cosa y se propaga tal cual.
      if (estados && cursor && err?.code === 'functions/invalid-argument') return { cursorRechazado: err }
      throw err
    }
    acumulado.push(...pagina.reportes)
    total = pagina.total
    if (onProgreso) onProgreso(acumulado.length, total)

    // Se terminó: el server dice que no quedan más páginas.
    if (!pagina.truncado || !pagina.cursorProximo) {
      return { reportes: acumulado, total, truncado: false }
    }
    // Toqué el techo: paro y lo DIGO. Que la pantalla siga andando con una
    // parte no es el problema; que no avise, sí.
    if (acumulado.length >= TECHO_CARGA) {
      return { reportes: acumulado, total, truncado: true }
    }
    cursor = pagina.cursorProximo
  }
  return { reportes: acumulado, total, truncado: true }
}

/** Detalle: contexto + comentarios + screenshot base64 via `verReporteDebug`. */
export async function cargarReporteDetalle(id) {
  const res = await call('verReporteDebug')({ id })
  return res.data
}

// ─── Cambiar estado / aprobar / rechazar (callables permisados) ─────────────

/**
 * Cambio de estado del lifecycle (gated server-side por `debugging.autorizar`).
 *
 * ⚠️ Puede rebotar con `reauth_required` cuando el destino es `nuevo`: desde el
 * report YyaW8SqqYok7KyK9RuNZ el step-up de Puerta Cero custodia el DESTINO (la
 * cola que la guardia ejecuta sin supervisión) y no la arista `informe → nuevo`.
 * El CALLER lo envuelve en `runSudo` — igual que `aprobarReporte`.
 */
export async function cambiarEstado(reportId, nuevoEstado, instruccion) {
  const args = { id: reportId, estado: nuevoEstado }
  // La instrucción de la decisión (ADR 2026-08-21): viaja al motor como motivo
  // de la transición Y al hilo como comentario — es lo que la guardia lee.
  if (instruccion) args.instruccion = String(instruccion).slice(0, 500)
  await call('cambiarEstadoDebug')(args)
}

// ─── T3: la validación del DUEÑO (UAT del reportador) ───────────────────────
// Allow-list PURA de las acciones del dueño sobre SU reporte — jamás derivada
// del map de ESTADO_LABELS (forense: gauge-por-resta). Espejo de la matriz
// `reportador` del motor (functions/debuggingMotor.js: desdeValidables +
// CIERRES_REABRIBLES); el candado real vive allá, EN la transacción — esto
// solo decide qué botones se ven.
export const ESTADOS_VALIDABLES_PROPIO = ['en_validacion', 'en_uat', 'verificado_auto']
export const CIERRES_REABRIBLES = ['cerrado', 'cerrado_sin_validar']

// T3-b (§4.6): los estados en SEGUNDA PERSONA para la vista del DUEÑO.
// Allow-list explícita con fallback — jamás derivado por resta del map admin
// (forense: gauge-por-resta). El tono mapea a los colores del design system.
const ESTADO_CRIOLLO = {
  informe:             { label: 'Recibido — esperando aprobación', tono: 'zinc' },
  nuevo:               { label: 'Lo estamos mirando', tono: 'blue' },
  en_revision:         { label: 'Lo estamos mirando', tono: 'blue' },
  en_curso:            { label: 'Lo están arreglando', tono: 'amber' },
  ajustado_ia:         { label: 'Arreglado — falta que llegue a la app', tono: 'violet' },
  deployado:           { label: 'El arreglo ya está en la app', tono: 'violet' },
  en_verificacion:     { label: 'Verificando el arreglo', tono: 'violet' },
  en_validacion:       { label: 'Deployado — podés probarlo', tono: 'violet' },
  en_uat:              { label: '👉 Te toca probarlo', tono: 'amber' },
  verificado_auto:     { label: 'Verificado — confirmalo vos', tono: 'teal' },
  cola_humana:         { label: 'Lo revisa una persona', tono: 'blue' },
  refutado:            { label: 'El arreglo no alcanzó — vuelve al agente', tono: 'red' },
  escalado:            { label: 'Escalado — lo mira el equipo', tono: 'red' },
  en_mesa:             { label: 'En el plan de Mariano', tono: 'blue' },
  planificado:         { label: 'En el plan de Mariano', tono: 'blue' },
  diferido:            { label: 'Lo absorbe un proyecto (más adelante)', tono: 'zinc' },
  en_investigacion:    { label: 'Lo estamos investigando', tono: 'blue' },
  en_comite:           { label: 'Lo están pensando los agentes', tono: 'blue' },
  cerrado_explicado:   { label: 'Respondido', tono: 'emerald' },
  cerrado:             { label: 'Cerrado', tono: 'emerald' },
  cerrado_sin_validar: { label: 'Se cerró sin tu validación — ¿quedó bien?', tono: 'lime' },
  sigue_con_errores:   { label: 'Reabierto — vuelve al agente esta noche', tono: 'red' },
  descartado:          { label: 'No se va a hacer', tono: 'zinc' },
  archivado:           { label: 'Cerrado', tono: 'emerald' },
}
export function estadoEnCriollo(estado) {
  return ESTADO_CRIOLLO[estado] || { label: 'En proceso', tono: 'zinc' }
}

export function accionesDePropio(reporte, uid) {
  if (!reporte || !uid || reporte.reportadorUid !== uid) return []
  if (ESTADOS_VALIDABLES_PROPIO.includes(reporte.estado)) return ['validar', 'rebotar']
  if (CIERRES_REABRIBLES.includes(reporte.estado)) return ['reabrir']
  return []
}

/**
 * Veredicto del dueño: 'resuelto' cierra, 'sigue_con_errores' rebota/reabre.
 * Gate server-side: `debugging.ver` + ownership y matriz en el MOTOR
 * (guardReportadorPropio en la transacción) — el front no decide nada.
 */
export async function validarReporte(reportId, veredicto, comentario) {
  const res = await call('validarReporteDebug')({ id: reportId, veredicto, comentario: comentario || null })
  return res.data
}

/**
 * Cambio de estado EN LOTE (pedido Mariano 10-ago-2026; gated server-side por
 * `debugging.autorizar`). El server valida CADA reporte y devuelve el resultado
 * parcial: { cambiados: [ids], excluidos: [{id, motivo}] }. `informe` queda
 * afuera siempre (se aprueba/rechaza por su propio botón — ADR 051) y las
 * notificaciones van agrupadas: una campana por reportador, no por reporte.
 */
export async function cambiarEstadoLote(ids, nuevoEstado) {
  const res = await call('cambiarEstadoDebugLote')({ ids, estado: nuevoEstado })
  return res.data
}

/**
 * PURO: ¿esta fila puede entrar a la selección del lote? `informe` no — su única
 * salida es aprobar/rechazar (sella `decisionHumana`, calibración IA). El server
 * lo re-valida; acá solo evita ofrecer un checkbox que va a rebotar.
 */
export function esElegibleParaLote(reporte) {
  return !!reporte && reporte.estado !== ESTADOS.INFORME
}

/** Reclasifica bug ↔ funcionalidad (gated por `debugging.autorizar`). Sirve
 * TAMBIÉN en `informe`: reclasificar antes de aprobar es el caso principal.
 * El tipo decide qué agente lo toma (bug → guardia nocturna). */
export async function cambiarTipo(reportId, tipo) {
  await call('cambiarTipoDebug')({ id: reportId, tipo })
}

/** Aprueba un informe -> 'nuevo' (gated por `debugging.autorizar` + WIZARD +
 * step-up server-side — Puerta Cero, ADR 083). Si el server rebota con
 * `reauth_required`, el CALLER lo envuelve en `runSudo` de SudoContext (el
 * flujo de la casa: reauth Google + TOTP si hay MFA, y reintento) — acá NO se
 * re-autentica: un popup crudo sin desafío MFA muere con
 * `auth/multi-factor-auth-required` (prueba de campo 12-ago). */
export async function aprobarReporte(reportId) {
  await call('autorizarReporteDebug')({ id: reportId, decision: 'aprobar' })
}

// ── Tanda: la firma en lote de Puerta Cero (diseño T4 §4.5, PR-3b) ──────────

/** Tope de tanda. 15 y no el MAX_LOTE_ESTADO de 100: aquél es de archivado,
 * donde el error es reversible. Aprobar de más le cuesta una noche a la
 * nocturna. */
export const MAX_TANDA = 15

/**
 * PURO — espejo del server (`functions/lib/triage/clasificador.js:405`), que
 * es la AUTORIDAD: acá sólo decide qué se ofrece en la UI; el server vuelve a
 * chequear y además RECOMPUTA el tier. Allow-list positiva: sin `triage` no es
 * "riesgo bajo", es *sin clasificar* (un informe creado a las 20:30, después
 * de la corrida, no entra).
 */
export function esElegibleParaTanda(r) {
  const t = r?.triage
  if (!t || !t.corridaEn) return false
  if (t.tier !== 'bajo') return false
  if (t.duplicadoDe) return false
  if (t.sugerido) return false
  return true
}

/** R7b (ADR 2026-09-07): aprueba un GRUPO como un solo arreglo — el principal
 * entra a la cola por la Puerta Cero de siempre y los duplicados quedan
 * descartados con `duplicadoConfirmadoDe`. Mismo step-up que aprobar: el
 * caller lo envuelve en `runSudo`. Devuelve { aprobado, descartados, excluidos }. */
export async function aprobarGrupo(principalId, duplicadosIds) {
  return call('aprobarGrupoDebug')({ principalId, duplicadosIds })
}

/** Aprueba DENTRO de una tanda: mismo callable que el click individual, con
 * `tanda: true` (activa la allow-list del server) y `version` ("se firma lo
 * que se leyó" — si el doc cambió, el motor aborta esa firma). */
export async function aprobarReporteEnTanda(reportId, version) {
  await call('autorizarReporteDebug')({ id: reportId, decision: 'aprobar', tanda: true, version })
}

/** ¿El error es el step-up esperado (abre el modal de sudo)? Espejo del shape
 * que `SudoContext.esReauthRequired`/`esSudoRequired` detectan — esos helpers
 * no están exportados y exportarlos para esto acoplaría la lib al contexto. */
export function esErrorDeStepUp(e) {
  const code = e?.details?.code
  return code === 'reauth_required' || code === 'sudo_required'
}

/**
 * Promueve un reporte al roadmap de desarrollo (ADR 14-ago-2026).
 *
 * Un click, cuatro pasos del lado del server: crea la tarjeta en el board y
 * mueve el reporte `informe → en_mesa → planificado → archivado`. NO pasa por
 * `nuevo` a propósito — ése es la cola que ficha la guardia nocturna, y sella
 * `decisionHumana: 'aprobar'`, que no es lo que pasó.
 *
 * Sin `runSudo`: el step-up server-side es del DESTINO `nuevo` (el motor lo
 * decide con `exigeStepUp`, report YyaW8SqqYok7KyK9RuNZ) y esta ruta nunca pasa
 * por ahí — va `en_mesa → planificado → archivado`. Envolverlo sería código
 * defensivo que nunca corre.
 *
 * Idempotente: `yaExistia: true` significa que la tarjeta ya estaba.
 * @returns {Promise<{roadmapItemId: string, yaExistia: boolean}>}
 */
export async function promoverAroadmap(reportId) {
  const res = await call('promoverReporteARoadmap')({ id: reportId })
  return res.data || {}
}

/** Rechaza un informe -> 'descartado' con motivo obligatorio (gated por `debugging.autorizar`). */
export async function rechazarReporte(reportId, motivo) {
  const texto = String(motivo || '').trim()
  if (!texto) throw new Error('El motivo del rechazo es obligatorio')
  await call('autorizarReporteDebug')({ id: reportId, decision: 'rechazar', motivo: texto })
}

// ─── Comentarios ──────────────────────────────────────────────────────────

/** Agrega un comentario al reporte (gated por `debugging.ver`). */
export async function agregarComentario(reportId, texto) {
  const t = String(texto || '').trim()
  if (!t) throw new Error('texto requerido')
  await call('comentarDebug')({ id: reportId, texto: t })
}

// ─── Helpers para UI ──────────────────────────────────────────────────────

export function fmtFecha(ts) {
  const d = fechaDe(ts)
  if (!d) return '—'
  return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

/**
 * PURO: fecha compacta para la tabla del listado ("10/08 21:30" — 24 h, sin
 * "a. m./p. m." ni coma). La columna Fecha era una de las que empujaba el
 * scroll horizontal (pedido Mariano 10-ago-2026).
 */
export function fmtFechaCorta(ts) {
  const d = fechaDe(ts)
  if (!d) return '—'
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mi = String(d.getMinutes()).padStart(2, '0')
  return `${dd}/${mm} ${hh}:${mi}`
}

// ═══════════════════════════════════════════════════════════════════════════
// Calibración de la IA (ADR 064, decisión 4 · tramo 6)
// ═══════════════════════════════════════════════════════════════════════════
//
// Mariano, 6-ago-2026: *"cuando vos lo reportes que diga si lo aprobaría o no y
// por qué, y yo hago el check final; si en un mes coincidimos en todo lo
// liberamos"*.
//
// Es el patrón correcto para automatizar un control: la decisión automática
// corre EN PARALELO a la humana, sin autoridad, y se mide la coincidencia antes
// de promover. Para que sirva, la coincidencia se REGISTRA, no se recuerda:
// `recomendacionIa` la sella el MCP al crear, `decisionHumana` la sella el
// callable de autorizar. Acá sólo se cuentan.
//
// ⚠️ EL NÚMERO SOLO NO ALCANZA, y el ADR lo dice: *"si el aprobador alguna vez
// rechazó. Un aprobador que nunca rechaza no es un control, y el número daría
// 'coincidimos siempre' sin significar nada"*. Por eso el resultado trae
// `degenerado`, con las dos mitades: si el humano nunca rechazó y si la IA nunca
// recomendó rechazar. Un 100% con cualquiera de las dos prendida no habilita
// nada.

export const RECOMENDACIONES = {
  APROBAR:  'aprobar',
  RECHAZAR: 'rechazar',
}

export const RECOMENDACION_LABELS = {
  aprobar:  { label: 'La aprobaría',  emoji: '👍' },
  rechazar: { label: 'La rechazaría', emoji: '👎' },
}

// T4-b: la sugerencia del triage LLM sobre el residuo (`triage.sugerido`).
// Enum cerrado, espejo de `lib/triage/residuo.js` del server. `no_se` es una
// abstención honesta, no un desacuerdo.
export const SUGERIDO_LABELS = {
  aprobar:  { label: 'Aprobaría',            emoji: '👍' },
  rechazar: { label: 'Rechazaría',           emoji: '👎' },
  no_se:    { label: 'No sabe (abstención)', emoji: '🤷' },
}

/**
 * PURO. Calibración del JUICIO del triage LLM (T4-b): `triage.sugerido` vs
 * `decisionHumana`. Mismo patrón que `calibracionIa` (ADR 064 dec. 4), con una
 * diferencia deliberada: `no_se` se cuenta como ABSTENCIÓN, no como error —
 * castigar la honestidad de "no sé" entrena al modelo a inventar.
 * @param {Array} reportes
 * @param {{mes?: string}} opts
 */
export function calibracionSugerido(reportes = [], { mes = 'todos' } = {}) {
  const conSugerido = (reportes || [])
    .filter(r => r.triage?.sugerido)
    .filter(r => !r.roadmapItemId)  // promover no es aprobar ni rechazar
    .filter(r => mes === 'todos' || mesDe(r.creadoEn) === mes)

  const abstenciones = conSugerido.filter(r => r.triage.sugerido === 'no_se').length
  // R7b: un duplicado confirmado en Puerta Cero no es aprobar ni rechazar —
  // el trabajo entró por el principal. No cuenta como desacuerdo.
  const comparables = conSugerido.filter(r => r.triage.sugerido !== 'no_se' && r.decisionHumana && r.decisionHumana !== 'duplicado')
  const pendientes = conSugerido.length - abstenciones - comparables.length

  let coincidencias = 0
  let humanoRechazo = 0
  for (const r of comparables) {
    if (r.triage.sugerido === r.decisionHumana) coincidencias += 1
    if (r.decisionHumana === RECOMENDACIONES.RECHAZAR) humanoRechazo += 1
  }
  const pct = (parte, total) => (total > 0 ? Math.round((parte / total) * 100) : null)
  return {
    total: comparables.length,
    abstenciones,
    pendientes,
    coincidencias,
    tasaPct: pct(coincidencias, comparables.length),
    humanoRechazo,
    degenerado: { humanoNuncaRechazo: comparables.length > 0 && humanoRechazo === 0 },
  }
}

/**
 * Coincidencia entre lo que la IA recomendó y lo que decidió la persona.
 * @param {Array} reportes
 * @param {{mes?: string}} opts — 'todos' (default) o 'YYYY-MM'
 */
export function calibracionIa(reportes = [], { mes = 'todos' } = {}) {
  const deIa = (reportes || [])
    .filter(r => origenDe(r) === ORIGENES.IA)
    // Promovidos afuera: un reporte que se mandó al roadmap NUNCA va a tener
    // `decisionHumana` —promover no es aprobar ni rechazar— así que contaría
    // como "sin triar" para siempre, inflando el número que este mismo panel
    // pide leer con cuidado. No se sella `decisionHumana: 'promover'`: sería
    // castigar a la IA por una opción que nunca tuvo.
    .filter(r => !r.roadmapItemId)
    .filter(r => mes === 'todos' || mesDe(r.creadoEn) === mes)

  // Comparables = los que tienen las DOS puntas. Un informe todavía sin triar
  // no es un desacuerdo: es un pendiente, y contarlo como error castigaría a la
  // IA por la velocidad del humano.
  const comparables = deIa.filter(r => r.recomendacionIa && r.decisionHumana && r.decisionHumana !== 'duplicado')  // R7b: duplicado confirmado ≠ desacuerdo
  const pendientes = deIa.length - comparables.length

  const porCategoria = new Map()
  let coincidencias = 0
  let humanoRechazo = 0
  let iaRecomendoRechazar = 0

  for (const r of comparables) {
    const cat = r.categoria || '—'
    let e = porCategoria.get(cat)
    if (!e) { e = { categoria: cat, n: 0, coincidencias: 0 }; porCategoria.set(cat, e) }
    e.n += 1
    if (r.recomendacionIa === r.decisionHumana) { e.coincidencias += 1; coincidencias += 1 }
    if (r.decisionHumana === RECOMENDACIONES.RECHAZAR) humanoRechazo += 1
    if (r.recomendacionIa === RECOMENDACIONES.RECHAZAR) iaRecomendoRechazar += 1
  }

  const pct = (parte, total) => (total > 0 ? Math.round((parte / total) * 100) : null)

  return {
    total: comparables.length,
    pendientes,
    coincidencias,
    tasaPct: pct(coincidencias, comparables.length),
    humanoRechazo,
    iaRecomendoRechazar,
    // Las dos formas de que el 100% no signifique nada.
    degenerado: {
      humanoNuncaRechazo: comparables.length > 0 && humanoRechazo === 0,
      iaNuncaRecomendoRechazar: comparables.length > 0 && iaRecomendoRechazar === 0,
    },
    porCategoria: [...porCategoria.values()]
      .map(e => ({ ...e, tasaPct: pct(e.coincidencias, e.n) }))
      .sort((a, b) => b.n - a.n || a.categoria.localeCompare(b.categoria)),
  }
}
